import { PropertyTypeNames, PropertyTypes } from 'constants/propertyTypes';
import { CountryCodes, CountryNames } from 'constants/countryCodes';
import { CURRENCY_CODES_BY_COUNTRY } from 'constants/currencies';
import {
  UnitOfMeasurement,
  UnitOfMeasurementCode,
  UnitOfMeasurementNames,
} from 'constants/unitOfMeasurement';
import { IPropertyCommon } from 'interfaces/IPropertyCommon';
import { IProperty } from 'interfaces/IProperty';
import { SearchOptionsType } from 'interfaces/ISearchOptionsType';
import { IdName } from 'interfaces/IdName';
import { ISpaceUse } from 'interfaces/ISpaceUse';
import { IPropertyInput } from 'interfaces/inputs/IPropertyInput';
import { getCurrentYear } from 'utils/date';

export const isOfficeBuilding = (property: IPropertyCommon) =>
  property.propertyType?.name === PropertyTypes.office && !!property.office;

export const isLifeScienceBuilding = (property: IPropertyCommon) =>
  property.propertyType?.name === PropertyTypes.lifeScience;

export const isIndustrialBuilding = (property: IPropertyCommon) =>
  [PropertyTypes.industrial, PropertyTypes.flexIndustrial].includes(
    property?.propertyType?.name,
  );
export const isIosBuilding = (property: IPropertyCommon) =>
  property?.propertyType?.name === PropertyTypes.ios;

export const isDataCenterBuilding = (property: IPropertyCommon) =>
  property?.propertyType?.name === PropertyTypes.dataCenter;

export const isMultifamilyBuilding = (property: IPropertyCommon) =>
  property?.propertyType?.name === PropertyTypes.multifamily;

export const isMultifamilyBuildingList = (properties: IPropertyCommon[]) => {
  const propertiesNames = properties.map(p => p.propertyType?.name);
  return propertiesNames.includes(PropertyTypes.multifamily);
};

export const isLandAndDevelopmentBuilding = (property: IPropertyCommon) =>
  property.propertyType?.name === PropertyTypes.landAndDevelopment;

export const isUKCountry = (country?: string) =>
  !!country &&
  (country === CountryNames.UK ||
    country === CountryCodes.UK ||
    country === CountryCodes.GB);

export const isUkProperty = (property: IProperty | undefined) => {
  if (!property) return false;
  return property.propertyCountry?.id === 4;
};

export const checkIfBuildingSizeIsRequired = (property: IPropertyCommon) => {
  const { propertyType } = property;
  const { industrial, office, flexIndustrial } = PropertyTypes;
  const typesWithRequiredBuildingSize = [industrial, office, flexIndustrial];

  return typesWithRequiredBuildingSize.some(
    type => propertyType?.name === type,
  );
};

export const convertIPropertyIntoIPropertyLookupInput = (
  property: IProperty | undefined,
  returnMarketPropertyIds?: boolean,
) => ({
  id: property?.id,
  primaryAddress: property?.primaryAddress,
  name: property?.name,
  city: property?.city,
  market: property?.market?.name,
  country: property?.country,
  postalCode: property?.postalCode,
  status: property?.status?.name,
  type: property?.type,
  ...(returnMarketPropertyIds && {
    marketId: property?.market?.id,
    propertyTypeId: property?.propertyType?.id,
  }),
});

/**
 * The property type name is used in many places in the app
 * Due to the FindComps URL refactoring we need to add the properties names using this function
 *
 * @param types
 * @returns
 */
export const getPropertyTypeNames = (types?: SearchOptionsType[]) => {
  let propertyTypesNames = types?.map(type => type.name).filter(Boolean);

  if (!propertyTypesNames?.length) {
    propertyTypesNames = types?.map(type => PropertyTypeNames[type.id]);
  }

  return propertyTypesNames;
};

export const getCurrentYearExpense = (property: IProperty) => {
  const currentYear = getCurrentYear();

  return property.expenses?.find(expense => expense.year === currentYear);
};

export const getSpaceUsesFromPropertyTypes = (
  spaceUses: ISpaceUse[] = [],
  propertyTypes: IdName[] = [],
) => {
  const propertyTypeIds = propertyTypes.map(pt => pt.id);

  return spaceUses.filter(spaceUse =>
    propertyTypeIds.includes(spaceUse.propertyTypeId),
  );
};

export const isUKProperty = (property: IProperty | IPropertyInput) =>
  property.propertyCountry?.code === UnitOfMeasurementCode.UK ||
  property.measurementSystem === UnitOfMeasurementCode.UK;

export const isCanadianProperty = (property: IProperty | IPropertyInput) =>
  property.currencyCode === CURRENCY_CODES_BY_COUNTRY[CountryCodes.CA] ||
  property?.propertyCountry?.code === CountryCodes.CA;

export const isUsProperty = (property: IProperty | IPropertyInput) =>
  property.currencyCode === CURRENCY_CODES_BY_COUNTRY[CountryCodes.US] ||
  property?.propertyCountry?.code === CountryCodes.US;

export const isSqmPropertyMeasurement = (
  property: IProperty | IPropertyInput,
) => {
  return (
    property.propertyCountry?.areaMeasurement === UnitOfMeasurement.sm ||
    property.measurementSystem === UnitOfMeasurementCode.EU
  );
};

export const isMeasurementMt = (property: IProperty) => {
  return (
    property.propertyCountry?.lengthMeasurement ===
    UnitOfMeasurementNames.meter.toLowerCase()
  );
};

export const getClearHeightAreaLabel = (property: IProperty) => {
  if (
    property.propertyCountry?.lengthMeasurement ===
    UnitOfMeasurementNames.meter.toLowerCase()
  ) {
    return UnitOfMeasurement.meter;
  }
  return '’';
};
export const isUkOrEuropePropertyMeasurement = (
  property: IProperty | IPropertyInput,
) => {
  const ukOrEuropeCodes = [UnitOfMeasurementCode.EU, UnitOfMeasurementCode.UK];

  return ukOrEuropeCodes.includes(
    property?.measurementSystem as UnitOfMeasurementCode,
  );
};
export const isUkOrEuropePropertyCode = (
  property: IProperty | IPropertyInput,
) => {
  const ukOrEuropeCodes = [UnitOfMeasurementCode.EU, UnitOfMeasurementCode.UK];

  return ukOrEuropeCodes.includes(
    property?.propertyCountry?.code as UnitOfMeasurementCode,
  );
};

export const getPropertyMeasurement = (property: IProperty) => {
  return property?.propertyCountry?.areaMeasurement ?? UnitOfMeasurement.sf;
};

export const getLatestRenovatedYear = (property: IProperty) => {
  const years = (property?.renovatedYears ?? [])
    .map(y => y?.year)
    .filter(Number);
  return years.length ? Math.max(...years) : null;
};
